import { Link } from 'react-router-dom';
import SEOPageLayout, { tokens } from '../../components/SEOPageLayout';
import { appLinks } from '../../lib/crossSiteLinks';

const sampleDriftEvent = {
  eventType: 'drift.detected',
  timestamp: '2026-01-02T09:15:33Z',
  repository: 'acme-corp/api-service',
  branch: 'main',
  baseline: {
    capturedAt: '2025-12-15T10:00:00Z',
    settings: {
      required_approving_review_count: 2,
      dismiss_stale_reviews: true,
      require_code_owner_reviews: true,
      required_status_checks: ['build', 'unit-tests', 'integration-tests'],
      enforce_admins: true,
      allow_force_pushes: false,
      allow_deletions: false,
    },
  },
  current: {
    capturedAt: '2026-01-02T09:15:33Z',
    settings: {
      required_approving_review_count: 1,
      dismiss_stale_reviews: true,
      require_code_owner_reviews: false,
      required_status_checks: ['build', 'unit-tests'],
      enforce_admins: true,
      allow_force_pushes: false,
      allow_deletions: false,
    },
  },
  changes: [
    {
      setting: 'required_approving_review_count',
      previous: 2,
      current: 1,
      severity: 'high',
      description: 'Minimum required approvals reduced',
    },
    {
      setting: 'require_code_owner_reviews',
      previous: true,
      current: false,
      severity: 'medium',
      description: 'Code owner review no longer required',
    },
    {
      setting: 'required_status_checks',
      previous: ['build', 'unit-tests', 'integration-tests'],
      current: ['build', 'unit-tests'],
      severity: 'high',
      description: 'Integration tests removed from required checks',
    },
  ],
  status: 'open',
};

export default function SampleDriftEventPage() {
  return (
    <SEOPageLayout
      title="Sample Drift Event - Branch Protection Change Detection"
      description="See an example of EvidentTrail drift detection identifying changes to GitHub branch protection settings. Anonymized sample data for illustration."
    >
      {/* Hero */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h1 className={`${tokens.heading.h1} mb-6`}>
              Sample Drift Event
            </h1>
            <p className={tokens.text.large}>
              This is an example of what EvidentTrail drift detection captures when 
              branch protection settings change. All data is anonymized.
            </p>
          </div>
        </div>
      </section>

      {/* Drift Summary */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Drift Detected</h2>
            <div className={`${tokens.card} border-amber-500/30`}>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">
                  3 Changes Detected
                </span>
                <span className="text-slate-400 text-sm">
                  {sampleDriftEvent.repository} / {sampleDriftEvent.branch}
                </span>
                <span className="text-slate-500 text-sm">
                  {new Date(sampleDriftEvent.timestamp).toLocaleString()}
                </span>
              </div>
              
              <div className="space-y-4">
                {sampleDriftEvent.changes.map((change, index) => (
                  <div key={index} className="bg-slate-900/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        change.severity === 'high' 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {change.severity}
                      </span>
                      <span className="font-mono text-sm text-slate-300">{change.setting}</span>
                    </div>
                    <p className={`${tokens.text.base} mb-2`}>{change.description}</p>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Previous:</span>
                        <pre className="text-red-400 mt-1">{JSON.stringify(change.previous, null, 2)}</pre>
                      </div>
                      <div>
                        <span className="text-slate-500">Current:</span>
                        <pre className="text-green-400 mt-1">{JSON.stringify(change.current, null, 2)}</pre>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Event JSON */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Complete Drift Event Record</h2>
            <div className={`${tokens.card} font-mono text-sm`}>
              <pre className="text-slate-300 overflow-x-auto">
                {JSON.stringify(sampleDriftEvent, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens Next */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Happens When Drift is Detected</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold">1</span>
                </div>
                <div>
                  <h3 className={`${tokens.heading.h3} mb-1`}>Alert Sent</h3>
                  <p className={tokens.text.base}>
                    You receive a notification that branch protection settings have changed.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold">2</span>
                </div>
                <div>
                  <h3 className={`${tokens.heading.h3} mb-1`}>Review the Change</h3>
                  <p className={tokens.text.base}>
                    Was this change intentional? Who made it? Is there a valid business reason?
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold">3</span>
                </div>
                <div>
                  <h3 className={`${tokens.heading.h3} mb-1`}>Take Action</h3>
                  <p className={tokens.text.base}>
                    Either restore the previous settings, or acknowledge the change with a documented reason.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Samples */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Related Sample Outputs</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/sample-timeline" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Sample Audit Timeline</h3>
                <p className={tokens.text.base}>Follow a complete chain of change control events.</p>
              </Link>
              <Link to="/sample-exception" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h3 className={`${tokens.heading.h3} mb-2`}>Sample Exception Record</h3>
                <p className={tokens.text.base}>See how exception handling is documented for auditors.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Catch Drift Before Auditors Do</h2>
            <p className={`${tokens.text.base} mb-8`}>
              Start monitoring your branch protection settings automatically.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/sample-exception" className="text-slate-300 hover:text-white transition-colors">
                See Sample Exception →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
